import ButtonComponent from 'components/button/buttonComponent';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron-down.svg';
import { ReactComponent as ChevronUpIcon } from 'assets/icons/chevron-up-fix.svg';
import React, { useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

export interface CardProps {
  title: string;
  className?: string;
  subtitle?: string | React.ReactNode;
  children: React.ReactNode;
  backgroundColor?: string;
  alwaysExpanded?: boolean;
  rightComponent?: React.ReactNode;
  hideMobileSubtitle?: boolean;
  borderedSubtitleDesktop?: boolean;
  showSeparator?: boolean;
  testId?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  rightComponentWrap?: boolean;
  defaultExpanded?: boolean;
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}

export const Card: React.FC<CardProps> = ({
  title,
  className,
  subtitle,
  children,
  backgroundColor = 'bg-white',
  alwaysExpanded = false,
  rightComponent = null,
  rightComponentWrap = true,
  hideMobileSubtitle = false,
  borderedSubtitleDesktop = false,
  showSeparator = false,
  defaultExpanded = false,
  testId,
  titleClassName = 'flex text-h4 text-dark-gray font-semibold',
  subtitleClassName,
  Icon,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const ArrowIcon = isExpanded ? ChevronUpIcon : ChevronDownIcon;
  const handleExpandIconClick = () => {
    setIsExpanded((isExpanded) => !isExpanded);
  };

  return (
    <div
      className={`flex flex-col items-center px-5 pt-5 pb-30 desktop:p-30 desktop:gap-5 gap-2.5 ${backgroundColor} w-full h-fit rounded-10`}
      data-testid={testId}
    >
      <div className="flex flex-col w-full">
        <div
          onClick={alwaysExpanded ? () => ({}) : handleExpandIconClick}
          className="flex flex-row w-full items-center justify-between p-0 gap-2.5 cursor-pointer"
        >
          <div
            className={twMerge('flex flex-row items-center gap-4', className)}
          >
            <div className="flex flex-row items-center gap-2">
              {!!Icon && <Icon className="w-6 h-6 mr-2.5" />}
              <h3 className={titleClassName}>{title}</h3>
            </div>

            {borderedSubtitleDesktop ? (
              <div className="px-2 self-center bg-black bg-opacity-5 rounded-5 flex-col justify-center items-center inline-flex">
                <div className="text-xs font-semibold">{subtitle}</div>
              </div>
            ) : (
              <div
                className={twJoin(
                  'text-xs hidden desktop:inline text-med-gray-3',
                  subtitleClassName,
                )}
              >
                {subtitle}
              </div>
            )}
          </div>
          <div className="flex flex-row items-center gap-2">
            <div
              className={twMerge(
                'desktop:flex flex-row items-center gap-2 ',
                rightComponentWrap ? 'hidden' : 'flex',
              )}
            >
              {rightComponent}
            </div>
            {!alwaysExpanded && (
              <ButtonComponent
                testID={'arrow-expand-icon'}
                type="underline"
                iconPosition="right"
                Icon={ArrowIcon}
                iconWidth="w-[12px]"
                fill="med-gray-3"
                stroke="med-gray-3"
              />
            )}
          </div>
        </div>
        {!hideMobileSubtitle && (
          <div
            className={twJoin(
              'text-xs self-start desktop:hidden text-med-gray-3',
              subtitleClassName,
            )}
          >
            {subtitle}
          </div>
        )}
        <div
          className={twMerge(
            'desktop:hidden',
            rightComponentWrap ? 'flex' : 'hidden',
          )}
        >
          {rightComponent}
        </div>
      </div>
      {showSeparator && (isExpanded || alwaysExpanded) && (
        <div className="w-full border-t border-gray-opacity-15" />
      )}
      {(isExpanded || alwaysExpanded) && children}
    </div>
  );
};
